/**
 * Tier 2 dynamic-block OBJECTS parsers — DYN-2.1 + 2.2 + Batch C.
 *
 * Decodes the ACDBBLOCK* records that AutoCAD emits for Tier 2 parameters
 * (Point / Polar / XY / Rotation / Lookup) and actions (Rotate / Scale /
 * Array / Lookup), plus the ACDBBLOCKPROPERTIESTABLE that carries lookup
 * tables and the user-defined block properties table.
 *
 * Batch C adds dimensional constraint parameters (Horizontal / Vertical /
 * Angular) emitted as ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER / *VERTICAL* /
 * *ANGULAR*. AutoCAD's actual class names for these are not fully public;
 * we choose the explicit names so round-trip is unambiguous and the
 * writer/parser pair stays symmetric.
 *
 * AutoCAD's internal DXF schema for dynamic blocks is largely
 * undocumented. Group codes used here are reverse-engineered from
 * AutoCAD-emitted files and confirmed against ezdxf's dynamic-block
 * reader. When we are unsure about a specific code we fall back to the
 * typed default (valueSet.mode = "none", radius = 0, etc.) rather than
 * throwing — the raw groups are still preserved on the
 * DxfDynamicBlockObject, so the writer can re-emit whatever we
 * originally saw.
 *
 * Paired with `dxfBridge.dynamic.writer.tier2.ts` (symmetric emitter).
 */
import { decodeDxfUnicode, type DxfDynamicBlockObject } from "@dxf-ai-editor/dxf-io";

import type {
  AngularConstraintParameter,
  ArrayAction,
  BlockPropertiesTable,
  BlockPropertiesTableColumn,
  BlockPropertiesTableKeyColumn,
  BlockPropertiesTableRow,
  BlockPropertyEntry,
  DynamicParameter,
  HorizontalConstraintParameter,
  LookupAction,
  LookupParameter,
  LookupTable,
  LookupTableColumn,
  LookupTableRow,
  PointParameter,
  PolarParameter,
  RotateAction,
  RotationParameter,
  ScaleAction,
  ValueSet,
  VerticalConstraintParameter,
  XYParameter,
} from "./dynamicBlock";
import {
  parseAngularConstraintParameterFromBpt,
  parseHorizontalConstraintParameterFromBpt,
  parseRadialConstraintParameterFromBpt,
  parseVerticalConstraintParameterFromBpt,
} from "./dxfBridge.dynamic.parser.constraints";
import {
  canonicalDynamicBlockClass,
  collectGroups,
  firstGroup,
  firstInt,
  firstNumber,
  parsePointAt,
} from "./dxfBridge.dynamic.shared";

// ═══════════════════════════════════════════════════════════════════════════
// Value-set decoder (shared by Point / Polar / XY / Rotation)
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD stores the value-set mode flag at codes 170 / 171 / 172 depending
// on the parameter kind (one axis per code). We accept the caller-supplied
// code and decode just that axis.

type AxisValueSetCodes = {
  modeCode: number;
  incrementCode: number;
  minCode: number;
  maxCode: number;
  listCode: number;
};

function parseAxisValueSet(
  obj: DxfDynamicBlockObject,
  codes: AxisValueSetCodes,
): ValueSet {
  const groups = obj.rawGroups;
  const modeRaw = firstInt(groups, codes.modeCode) ?? 0;
  const mode: ValueSet["mode"] =
    modeRaw === 1 ? "increment" : modeRaw === 2 ? "list" : "none";
  const listRaw = collectGroups(groups, codes.listCode).map(Number.parseFloat);
  const listValues = listRaw.filter(Number.isFinite);
  const valueSet: ValueSet = {
    mode,
    increment: firstNumber(groups, codes.incrementCode) ?? 0,
    listValues,
  };
  const min = firstNumber(groups, codes.minCode);
  const max = firstNumber(groups, codes.maxCode);
  if (min !== undefined) valueSet.min = min;
  if (max !== undefined) valueSet.max = max;
  return valueSet;
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 2 parameters
// ═══════════════════════════════════════════════════════════════════════════

function parsePointParameter(obj: DxfDynamicBlockObject): PointParameter {
  const groups = obj.rawGroups;
  const basePoint = parsePointAt(groups, 10, 20);
  const currentPoint = parsePointAt(groups, 11, 21);

  // xValueSet: codes 170 / 140 / 141 / 142 / 145 (best-effort mapping).
  const xValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 140,
    minCode: 141,
    maxCode: 142,
    listCode: 145,
  });
  // yValueSet: codes 171 / 143 / 144 / 146 / 147 (best-effort mapping).
  const yValueSet = parseAxisValueSet(obj, {
    modeCode: 171,
    incrementCode: 143,
    minCode: 144,
    maxCode: 146,
    listCode: 147,
  });

  return {
    kind: "point",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `point_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    basePoint,
    currentPoint,
    xValueSet,
    yValueSet,
  };
}

function parsePolarParameter(obj: DxfDynamicBlockObject): PolarParameter {
  const groups = obj.rawGroups;
  const basePoint = parsePointAt(groups, 10, 20);
  const secondPoint = parsePointAt(groups, 11, 21);

  // AutoCAD uses 140 = defaultDistance, 141 = currentDistance; 50 / 51 for
  // default and current angle (degrees).
  const defaultDistance = firstNumber(groups, 140) ?? 0;
  const currentDistance = firstNumber(groups, 141) ?? defaultDistance;
  const defaultAngle = firstNumber(groups, 50) ?? 0;
  const currentAngle = firstNumber(groups, 51) ?? defaultAngle;

  const distanceValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  const angleValueSet = parseAxisValueSet(obj, {
    modeCode: 171,
    incrementCode: 146,
    minCode: 147,
    maxCode: 148,
    listCode: 149,
  });

  return {
    kind: "polar",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `polar_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    basePoint,
    secondPoint,
    defaultDistance,
    defaultAngle,
    currentDistance,
    currentAngle,
    distanceValueSet,
    angleValueSet,
  };
}

function parseXYParameter(obj: DxfDynamicBlockObject): XYParameter {
  const groups = obj.rawGroups;
  const basePoint = parsePointAt(groups, 10, 20);
  const secondPoint = parsePointAt(groups, 11, 21);

  const defaultX = firstNumber(groups, 140) ?? 0;
  const defaultY = firstNumber(groups, 141) ?? 0;
  const currentX = firstNumber(groups, 142) ?? defaultX;
  const currentY = firstNumber(groups, 143) ?? defaultY;

  const xValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 144,
    minCode: 145,
    maxCode: 146,
    listCode: 147,
  });
  const yValueSet = parseAxisValueSet(obj, {
    modeCode: 171,
    incrementCode: 148,
    minCode: 149,
    maxCode: 1070,
    listCode: 1071,
  });

  return {
    kind: "xy",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `xy_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    basePoint,
    secondPoint,
    defaultX,
    defaultY,
    currentX,
    currentY,
    xValueSet,
    yValueSet,
  };
}

function parseRotationParameter(
  obj: DxfDynamicBlockObject,
): RotationParameter {
  const groups = obj.rawGroups;
  const basePoint = parsePointAt(groups, 10, 20);
  const radius = firstNumber(groups, 40) ?? 1;
  const defaultAngle = firstNumber(groups, 50) ?? 0;
  const currentAngle = firstNumber(groups, 51) ?? defaultAngle;

  const angleValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });

  return {
    kind: "rotation",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `rotation_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    basePoint,
    radius,
    defaultAngle,
    currentAngle,
    angleValueSet,
  };
}

function parseLookupParameter(
  obj: DxfDynamicBlockObject,
  tableHandleToId: Map<string, string>,
): LookupParameter {
  const groups = obj.rawGroups;
  const position = parsePointAt(groups, 10, 20);
  const tableHandle = firstGroup(groups, 340)?.trim() ?? "";
  const lookupTableId = tableHandleToId.get(tableHandle) ?? tableHandle;
  const currentRowHandle = firstGroup(groups, 341)?.trim();
  const reverseLookup = (firstInt(groups, 290) ?? 0) === 1;
  return {
    kind: "lookup",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `lookup_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    position,
    reverseLookup,
    lookupTableId: lookupTableId || `table_${obj.handle}`,
    ...(currentRowHandle && currentRowHandle.length > 0
      ? { currentLookupRowId: currentRowHandle }
      : {}),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 2 constraint parameters (DYN-2 Batch C)
// ═══════════════════════════════════════════════════════════════════════════
//
// Group codes for constraint params (best-effort reverse-engineered):
//   1   = name
//   3   = label
//   4   = description
//   10/20 = pointA (x / y)
//   11/21 = pointB (x / y)
//   12/22 = vertex (angular only)
//   140 = defaultDistance / defaultAngle
//   141 = currentDistance / currentAngle
//   170 = distance/angle valueSet mode
//   142–145 = valueSet increment/min/max/list
//   280 = chainActions
//   281 = showGrip

function parseHorizontalConstraintParameter(
  obj: DxfDynamicBlockObject,
): HorizontalConstraintParameter {
  const groups = obj.rawGroups;
  const pointA = parsePointAt(groups, 10, 20);
  const pointB = parsePointAt(groups, 11, 21);
  const defaultDistance = firstNumber(groups, 140) ?? 0;
  const currentDistance = firstNumber(groups, 141) ?? defaultDistance;
  const distanceValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  return {
    kind: "constraint-horizontal",
    id: obj.handle,
    name:
      firstGroup(groups, 1)?.trim() ?? `constraint_horizontal_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    pointA,
    pointB,
    defaultDistance,
    currentDistance,
    distanceValueSet,
    associatedEntities: [],
  };
}

function parseVerticalConstraintParameter(
  obj: DxfDynamicBlockObject,
): VerticalConstraintParameter {
  const groups = obj.rawGroups;
  const pointA = parsePointAt(groups, 10, 20);
  const pointB = parsePointAt(groups, 11, 21);
  const defaultDistance = firstNumber(groups, 140) ?? 0;
  const currentDistance = firstNumber(groups, 141) ?? defaultDistance;
  const distanceValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  return {
    kind: "constraint-vertical",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `constraint_vertical_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    pointA,
    pointB,
    defaultDistance,
    currentDistance,
    distanceValueSet,
    associatedEntities: [],
  };
}

function parseAngularConstraintParameter(
  obj: DxfDynamicBlockObject,
): AngularConstraintParameter {
  const groups = obj.rawGroups;
  // Angular constraint stores three points: pointA / pointB rays and
  // a vertex pivot. Codes 12/22 are the vertex (matches AutoCAD's
  // common "third point" pattern).
  const pointA = parsePointAt(groups, 10, 20);
  const pointB = parsePointAt(groups, 11, 21);
  const vertex = parsePointAt(groups, 12, 22);
  const defaultAngle = firstNumber(groups, 140) ?? firstNumber(groups, 50) ?? 0;
  const currentAngle =
    firstNumber(groups, 141) ?? firstNumber(groups, 51) ?? defaultAngle;
  const angleValueSet = parseAxisValueSet(obj, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  return {
    kind: "constraint-angular",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? `constraint_angular_${obj.handle}`,
    label: firstGroup(groups, 3)?.trim() ?? "",
    description: firstGroup(groups, 4)?.trim() ?? "",
    chainActions: (firstInt(groups, 280) ?? 0) === 1,
    showGrip: (firstInt(groups, 281) ?? 1) !== 0,
    vertex,
    pointA,
    pointB,
    defaultAngle,
    currentAngle,
    angleValueSet,
    associatedEntities: [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 2 actions
// ═══════════════════════════════════════════════════════════════════════════
//
// The Tier 2 actions share the parameter-handle + selection-set encoding
// with Tier 1 (330 = owner / parameter; 340[] = parameter + selection
// entity handles — first one is the parameter, the rest are selection
// entities). The per-kind fields come after.

function extractParameterAndSelection(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): { parameterId: string; selectionEntityIds: string[] } {
  const groups = obj.rawGroups;
  const parameterHandle =
    firstGroup(groups, 330)?.trim() ?? firstGroup(groups, 340)?.trim() ?? "";
  const parameterId = handleToParamId.get(parameterHandle) ?? parameterHandle;
  const selectionEntityIds = collectGroups(groups, 340)
    .slice(1) // first 340 is the parameter handle
    .map((v) => v.trim())
    .filter((h) => h.length > 0);
  return { parameterId, selectionEntityIds };
}

function parseRotateAction(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): RotateAction {
  const groups = obj.rawGroups;
  const { parameterId, selectionEntityIds } = extractParameterAndSelection(
    obj,
    handleToParamId,
  );
  return {
    kind: "rotate",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? "",
    parameterId,
    selectionEntityIds,
    angleMultiplier: firstNumber(groups, 40) ?? 1,
  };
}

function parseScaleAction(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): ScaleAction {
  const groups = obj.rawGroups;
  const { parameterId, selectionEntityIds } = extractParameterAndSelection(
    obj,
    handleToParamId,
  );
  const scaleTypeRaw = firstInt(groups, 170) ?? 0;
  const scaleType: ScaleAction["scaleType"] =
    scaleTypeRaw === 1 ? "x-only" : scaleTypeRaw === 2 ? "y-only" : "uniform";
  return {
    kind: "scale",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? "",
    parameterId,
    selectionEntityIds,
    scaleType,
    scaleMultiplier: firstNumber(groups, 40) ?? 1,
  };
}

function parseArrayAction(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): ArrayAction {
  const groups = obj.rawGroups;
  const { parameterId, selectionEntityIds } = extractParameterAndSelection(
    obj,
    handleToParamId,
  );
  return {
    kind: "array",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? "",
    parameterId,
    selectionEntityIds,
    columnSpacing: firstNumber(groups, 42) ?? 0,
    rowSpacing: firstNumber(groups, 43) ?? 0,
  };
}

function parseLookupAction(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): LookupAction {
  const groups = obj.rawGroups;
  // Lookup actions only reference a single parameter (the driving
  // LookupParameter); they have no selection set (AutoCAD uses the
  // cascade to propagate values to input parameters — no geometry).
  const parameterHandle =
    firstGroup(groups, 340)?.trim() ?? firstGroup(groups, 330)?.trim() ?? "";
  const parameterId = handleToParamId.get(parameterHandle) ?? parameterHandle;
  return {
    kind: "lookup",
    id: obj.handle,
    name: firstGroup(groups, 1)?.trim() ?? "",
    parameterId,
    selectionEntityIds: [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Block properties / lookup table
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD uses the same class (ACDBBLOCKPROPERTIESTABLE) for both the
// user-defined block properties table (BTABLE) and for the lookup tables
// referenced by LookupParameter.lookupTableId. Both payloads share a
// column-list + row-list skeleton; we inspect the table's row cells to
// tell which one we've got:
//   * BTABLE    — rows carry only an outputValue per entry (the default
//                 value). Input columns are the property names.
//   * LOOKUP    — each row's inputValues map lookupTableColumnId → value;
//                 the row optionally carries an outputValue.
//
// The top-level decision "is this a lookup table or a block properties
// table" is made by the bridge (`parseBlockPropertiesTable` returns both
// representations and the caller pins them based on whether the table is
// referenced by a LookupParameter).

type ParsedTable = {
  lookupTable: LookupTable;
  blockPropertiesTable: BlockPropertiesTable | undefined;
};

// Helper: coerce a raw DXF cell value (string, possibly numeric) into the
// typed `number | string | boolean` we want on LookupTableRow.inputValues.
// The numeric branch checks that `Number(raw)` matches round-trip ToString
// form — avoids false-positives for strings like "1.23abc" that
// Number.parseFloat would accept.
function coerceCell(raw: string): number | string | boolean {
  const trimmed = raw.trim();
  if (trimmed === "1" || trimmed.toLowerCase() === "true") return true;
  if (trimmed === "0" || trimmed.toLowerCase() === "false") return false;
  const n = Number(trimmed);
  if (Number.isFinite(n) && trimmed.length > 0 && String(n) === trimmed) {
    return n;
  }
  return trimmed;
}

/**
 * Decode an ACDBBLOCKPROPERTIESTABLE into a `LookupTable` + optional
 * `BlockPropertiesTable`. Both come out of the same DXF object because
 * AutoCAD re-uses the class for both purposes. The caller picks whichever
 * representation fits the table's usage context.
 *
 * Group codes (best-effort; AutoCAD's encoding is undocumented):
 *   1   → table name
 *   2   → column label (repeating) — legacy BTABLE form
 *   3   → row output label (repeating) — legacy BTABLE form
 *   91  → column count
 *   92  → row count (legacy BTABLE form)
 *   93  → row count (DYN-4 Fix 5 constraint-based form)
 *   340 → input parameter handles (repeating; one per column) — legacy
 *   301 → row cell values (repeating; columnCount per row) — legacy
 *   302 → row output values (repeating; one per row; optional) — legacy
 *   70  → per-column property type (0=string, 1=number, 2=boolean) —
 *         only set for the legacy BTABLE usage; LOOKUP usage omits it.
 *
 * DYN-4 Fix 5 — constraint-based key-column form additionally decodes a
 * full structured table:
 *   After `90 columnCount`, for each column (in order):
 *     91  column index       92  column kind        301 column class
 *     300 display name       340 link handle        170 value type
 *   After `93 rowCount`, for each row (in order):
 *     90  row index          170 row state flag     300 row key / cell strings
 *     per column:            170 cell type          140 numeric OR 300 string
 *
 * The constraint-based walker runs in parallel to the legacy decoder and
 * only populates `columns` / `rows` / `keyColumn` when a `90 columnCount`
 * and per-column `92 kind` codes are present — legacy BTABLE writers
 * omit both, so legacy round-trip stays on the `entries[]` path.
 */
export function parseBlockPropertiesTable(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
): ParsedTable {
  const groups = obj.rawGroups;
  const name = firstGroup(groups, 1)?.trim() ?? `table_${obj.handle}`;

  // DYN-4 Fix 5 — attempt the constraint-based structured decode first.
  // It consumes group-90 (column count) + per-column group-91/92/… in
  // declaration order. A positive match means this record is a
  // constraint-based table (АБИ1.dxf ibeam pattern); the legacy BTABLE
  // decoder below is skipped because its 91/301/340 collectors would
  // otherwise double-count cells.
  const constraintTable = decodeConstraintPropertiesTable(obj);

  if (constraintTable) {
    // Constraint-based tables are NOT lookup tables — legacy parameter
    // choices live in `columns`/`rows`. We still return an empty
    // LookupTable shape so downstream `tableHandleToId` stays keyed by
    // handle; the metadata builder will discard it once it detects the
    // constraint form via the non-empty columns array on the paired
    // blockPropertiesTable.
    const emptyLookup: LookupTable = {
      id: obj.handle,
      name,
      inputColumns: [],
      rows: [],
    };
    return { lookupTable: emptyLookup, blockPropertiesTable: constraintTable };
  }

  const columnCount = firstInt(groups, 91) ?? 0;
  const rowCount = firstInt(groups, 92) ?? 0;
  const columnLabels = collectGroups(groups, 2).map((s) => s.trim());
  const inputParamHandles = collectGroups(groups, 340).map((s) => s.trim());
  const rowCells = collectGroups(groups, 301); // columnCount per row
  const rowOutputs = collectGroups(groups, 302); // one per row (optional)
  const columnTypes = collectGroups(groups, 70)
    .map((s) => Number.parseInt(s, 10))
    .filter((n) => Number.isFinite(n));

  // Lookup table input columns — use the parameter handle mapping if
  // available, fall back to the raw handle so round-trip preserves it.
  const inputColumns: LookupTableColumn[] = [];
  for (let i = 0; i < columnCount; i += 1) {
    const handle = inputParamHandles[i] ?? "";
    const mappedId =
      handleToParamId.get(handle) ?? (handle.length > 0 ? handle : `col_${i}`);
    // AutoCAD column key defaults to `currentDistance` when we cannot
    // infer a better key from context. The bridge layer can re-assign the
    // propertyKey once it sees the parameter's kind — we store the
    // safest default here.
    const column: LookupTableColumn = {
      id: `${obj.handle}_col_${i}`,
      parameterId: mappedId,
      propertyKey: "currentDistance",
    };
    inputColumns.push(column);
  }

  // Lookup table rows — split the flat 301 string list into chunks of
  // length `columnCount`. AutoCAD stores cells in row-major order.
  const rows: LookupTableRow[] = [];
  for (let r = 0; r < rowCount; r += 1) {
    const inputValues: Record<string, number | string | boolean> = {};
    for (let c = 0; c < columnCount; c += 1) {
      const cell = rowCells[r * columnCount + c];
      if (cell === undefined) continue;
      const col = inputColumns[c];
      if (!col) continue;
      inputValues[col.id] = coerceCell(cell);
    }
    const outputRaw = rowOutputs[r];
    const row: LookupTableRow = {
      id: `${obj.handle}_row_${r}`,
      inputValues,
      ...(outputRaw !== undefined ? { outputValue: coerceCell(outputRaw) } : {}),
    };
    rows.push(row);
  }

  const lookupTable: LookupTable = {
    id: obj.handle,
    name,
    inputColumns,
    rows,
  };

  // Block-properties representation — only populated when AutoCAD wrote a
  // per-column type list (group 70), otherwise we leave it undefined and
  // the caller keeps the LookupTable view.
  let blockPropertiesTable: BlockPropertiesTable | undefined;
  if (columnTypes.length === columnCount && columnCount > 0) {
    const entries: BlockPropertyEntry[] = [];
    for (let i = 0; i < columnCount; i += 1) {
      const typeRaw = columnTypes[i] ?? 0;
      const type: BlockPropertyEntry["type"] =
        typeRaw === 1 ? "number" : typeRaw === 2 ? "boolean" : "string";
      entries.push({
        key: columnLabels[i] ?? `prop_${i}`,
        label: columnLabels[i] ?? `Property ${i + 1}`,
        type,
        ...(rowOutputs[i] !== undefined
          ? { defaultValue: coerceCell(rowOutputs[i] as string) }
          : {}),
      });
    }
    // Populate the DYN-4 Fix 5 fields with defaults so downstream code
    // can index `bpt.columns` / `bpt.rows` without defensive checks —
    // legacy BTABLE sources never carry the constraint-based shape.
    blockPropertiesTable = {
      entries,
      id: obj.handle,
      handle: obj.handle,
      name,
      columns: [],
      rows: [],
    };
  }

  return { lookupTable, blockPropertiesTable };
}

// ─── DYN-4 Fix 5 — constraint-based properties table decoder ───────────────
//
// Walks the raw group stream positionally so the parser can tell apart
// column-header groups from row-cell groups that share the same codes
// (170, 300). Detection is "if a `90 columnCount` is present we attempt
// this shape"; when the table turns out to be the legacy BTABLE form
// (columnCount reaches past the end of declared columns, no per-column
// `92 kind`) the helper returns undefined and the caller keeps the
// existing entries[] path intact.

const COLUMN_KIND_BY_CODE: Record<
  number,
  BlockPropertiesTableColumn["kind"]
> = {
  0: "user-variable",
  1: "updated-distance",
  2: "updated-angle",
  3: "updated-real",
  5: "updated-string",
};

const VALUE_TYPE_BY_CODE: Record<
  number,
  BlockPropertiesTableColumn["valueType"]
> = {
  0: "real",
  1: "distance",
  2: "area",
  3: "angle",
  5: "string",
};

function decodeColumnKind(
  raw: number | undefined,
): BlockPropertiesTableColumn["kind"] {
  if (raw === undefined) return "unknown";
  return COLUMN_KIND_BY_CODE[raw] ?? "unknown";
}

function decodeValueType(
  raw: number | undefined,
): BlockPropertiesTableColumn["valueType"] {
  if (raw === undefined) return "unknown";
  return VALUE_TYPE_BY_CODE[raw] ?? "unknown";
}

function decodeConstraintPropertiesTable(
  obj: DxfDynamicBlockObject,
): BlockPropertiesTable | undefined {
  // Try real AutoCAD 2018+ shape first (АБИ1.dxf fixture).
  const real = decodeRealAutoCadPropertiesTable(obj);
  if (real) return real;

  // Fall back to synthetic-writer shape (92-kind encoding used by the
  // round-trip tests in `dxfBridge.dynamic.writer.bpt.ts`).
  return decodeSyntheticPropertiesTable(obj);
}

// DYN-4 Phase 7 — decoder for the REAL AutoCAD 2018+ BLOCKPROPERTIESTABLE
// encoding (АБИ1.dxf). The structure after the `100 AcDbBlockPropertiesTable`
// subclass marker is:
//
//   100 AcDbBlockPropertiesTable
//    90 <N>                   ← count of *something* (grip slots?), not colCount
//   300 <tableDisplayName>
//   301 <empty / legacy>
//    91 <columnCount>
//   340 <col[0].linkHandle>        ← SPECIAL: col 0 has leading linkHandle
//   170 0  171 -1  300 <>  301 <kind>  90 <idx>  170 / 170 / 290-294 / 302
//   340 0  340 <col[1].linkHandle>  ← trailing 340: null + next linkHandle
//   170 0  171 -1  300 <>  301 <kind>  90 <idx>  170 / 170 / 290-294 / 302
//   340 0  340 <col[2].linkHandle>
//   …
//   340 0                          ← last col: only ONE trailing 340 (null)
//    92 <rowCount>
//   (rowCount × row blocks — see below)
//
// Each row block:
//    90 <rowIndex>
//   170 <rowFlag>
//   300 <rowKey>
//   per column: 170 <valueType>, then 140 <number> OR 300 <string>
//
// The discriminator that tells this from the synthetic writer encoding is
// the presence of `301 UserVariable` or `301 Updated…` markers — the
// synthetic writer never emits those strings; it uses `92 <int>` instead.
//
// Empirical note on linkHandle layout: AutoCAD writes column N's linkHandle
// as the SECOND `340` after column N-1's `302` separator (the first `340`
// is always "0" / null). Column 0's linkHandle is the SINGLE `340` right
// before column 0's `170 0 171 -1 300`. This layout matches what we see in
// АБИ1.dxf (ibeam block, handle 4E3B) for all 9 columns.
function decodeRealAutoCadPropertiesTable(
  obj: DxfDynamicBlockObject,
): BlockPropertiesTable | undefined {
  const groups = obj.rawGroups;

  // Find the AcDbBlockPropertiesTable subclass marker.
  let subclassIdx = -1;
  for (let i = 0; i < groups.length; i += 1) {
    const g = groups[i];
    if (
      g &&
      g.code === 100 &&
      g.value.trim() === "AcDbBlockPropertiesTable"
    ) {
      subclassIdx = i;
      break;
    }
  }
  if (subclassIdx < 0) return undefined;

  // Scan forward for the first 300 after subclass — table display name.
  // Also locate the first 91 after subclass — columnCount.
  let displayName = "";
  let columnCount = -1;
  let colStart = -1;
  for (let i = subclassIdx + 1; i < groups.length; i += 1) {
    const g = groups[i];
    if (!g) continue;
    if (displayName === "" && g.code === 300) {
      displayName = decodeDxfUnicode(g.value).trim(); // T9 PR-3 — table name decode
    }
    if (g.code === 91) {
      const parsed = Number.parseInt(g.value, 10);
      if (Number.isFinite(parsed)) {
        columnCount = parsed;
        colStart = i + 1;
        break;
      }
    }
  }
  if (columnCount < 0 || colStart < 0) return undefined;

  // First pass — find all 301 markers (one per column) and use their
  // positions to delimit columns. A 301 inside this scope is either a
  // column-kind marker (UserVariable / UpdatedDistance / …) or an empty
  // legacy separator; only the non-empty ones count.
  const kindMarkerIdx: number[] = [];
  let rowSectionIdx = -1;
  for (let i = colStart; i < groups.length; i += 1) {
    const g = groups[i];
    if (!g) continue;
    if (g.code === 92) {
      // `92 <rowCount>` marks the end of the columns section.
      rowSectionIdx = i;
      break;
    }
    if (g.code === 301 && columnKindFromMarker(g.value.trim()) !== "unknown") {
      kindMarkerIdx.push(i);
      if (kindMarkerIdx.length > columnCount) break;
    }
  }
  if (kindMarkerIdx.length === 0) return undefined;

  // Second pass — walk each column using its 301 marker as the anchor.
  //   linkHandle:
  //     col 0 → the last `340` BEFORE kindMarker[0] within [colStart, mk[0])
  //     col N>0 → the second `340` between kindMarker[N-1]+1 and kindMarker[N]
  //               (AutoCAD writes `340 0  340 <next-link>` as the trailing
  //                pair of col N-1; the `340 <next-link>` is col N's link).
  //   displayName:
  //     col 0 → the `300` AFTER kindMarker[0] (e.g. "Пользовательский")
  //     col N>0 → the first `300` AFTER kindMarker[N] if any (most
  //               UpdatedDistance columns emit empty 300 — leave blank)
  //   valueType:
  //     first `170` AFTER kindMarker[N] that is not -9999 (those are
  //     "no-range" flags; the real valueType sits before them on col 0
  //     but is absent on Updated* — leave `unknown` when not clear).
  const columns: BlockPropertiesTableColumn[] = [];
  for (let c = 0; c < Math.min(columnCount, kindMarkerIdx.length); c += 1) {
    const markerGroup = groups[kindMarkerIdx[c] as number];
    const marker = markerGroup ? markerGroup.value.trim() : "";
    const kind = columnKindFromMarker(marker);
    const col: BlockPropertiesTableColumn = {
      index: c,
      kind,
      columnName: marker,
      displayName: "",
      linkHandle: "",
      valueType: "unknown",
    };

    // linkHandle resolution.
    if (c === 0) {
      // Scan backward from marker[0] to colStart for the LAST 340.
      for (let i = (kindMarkerIdx[0] as number) - 1; i >= colStart; i -= 1) {
        const g = groups[i];
        if (g && g.code === 340) {
          col.linkHandle = g.value.trim();
          break;
        }
      }
    } else {
      // Scan between marker[c-1] and marker[c] for ALL 340 groups; pick
      // the LAST one whose value is non-zero (the "340 0" is a null
      // separator).
      const lo = (kindMarkerIdx[c - 1] as number) + 1;
      const hi = kindMarkerIdx[c] as number;
      for (let i = hi - 1; i >= lo; i -= 1) {
        const g = groups[i];
        if (g && g.code === 340) {
          const v = g.value.trim();
          if (v !== "0" && v !== "") {
            col.linkHandle = v;
            break;
          }
        }
      }
    }

    // displayName — first non-empty 300 AFTER the marker, before the next
    // marker or the row section.
    const nextBoundary =
      c + 1 < kindMarkerIdx.length
        ? (kindMarkerIdx[c + 1] as number)
        : rowSectionIdx >= 0
          ? rowSectionIdx
          : groups.length;
    for (let i = (kindMarkerIdx[c] as number) + 1; i < nextBoundary; i += 1) {
      const g = groups[i];
      if (g && g.code === 300) {
        const v = decodeDxfUnicode(g.value).trim(); // T9 PR-3 — column name decode
        if (v.length > 0) {
          col.displayName = v;
          break;
        }
      }
    }

    columns.push(col);
  }

  let cursor = rowSectionIdx >= 0 ? rowSectionIdx : groups.length;
  let rowCount = -1;
  let rowStart = -1;
  if (rowSectionIdx >= 0) {
    const g = groups[rowSectionIdx];
    if (g && g.code === 92) {
      const parsed = Number.parseInt(g.value, 10);
      if (Number.isFinite(parsed)) {
        rowCount = parsed;
        rowStart = rowSectionIdx + 1;
      }
    }
  }
  void cursor;

  const rows: BlockPropertiesTableRow[] = [];
  if (rowCount >= 0 && rowStart >= 0) {
    cursor = rowStart;
    for (let r = 0; r < rowCount; r += 1) {
      // Row starts at `90 <rowIndex>`.
      while (cursor < groups.length) {
        const g = groups[cursor];
        if (!g) break;
        if (g.code === 90) break;
        cursor += 1;
      }
      if (cursor >= groups.length) break;

      const rowIndexGroup = groups[cursor];
      const rowIndex =
        rowIndexGroup && rowIndexGroup.code === 90
          ? Number.parseInt(rowIndexGroup.value, 10)
          : r;
      cursor += 1;

      const row: BlockPropertiesTableRow = {
        index: Number.isFinite(rowIndex) ? rowIndex : r,
        flag: 1,
        key: "",
        values: [],
      };

      // Next: 170 <flag>, 300 <key>, then per-column cells.
      let seenFlag = false;
      let seenKey = false;
      let pendingNumeric = false;
      let pendingString = false;
      while (cursor < groups.length && row.values.length < columns.length) {
        const sg = groups[cursor];
        if (!sg) break;
        if (sg.code === 90) break; // next row

        if (sg.code === 170) {
          if (!seenFlag) {
            const flagRaw = Number.parseInt(sg.value, 10);
            if (Number.isFinite(flagRaw)) row.flag = flagRaw;
            seenFlag = true;
            cursor += 1;
            continue;
          }
          // Subsequent 170 = cell value-type — determine what code
          // carries the cell value. AutoCAD uses 40 for a double
          // expression and 140 for a row cell; for strings, the value
          // follows as 300.
          const vtRaw = Number.parseInt(sg.value, 10);
          pendingString = vtRaw === 5;
          pendingNumeric = !pendingString;
          cursor += 1;
          continue;
        }
        if (sg.code === 300) {
          if (!seenKey) {
            row.key = decodeDxfUnicode(sg.value); // T9 PR-3 — row key decode
            seenKey = true;
            cursor += 1;
            continue;
          }
          if (pendingString) {
            row.values.push(decodeDxfUnicode(sg.value)); // T9 PR-3 — string cell decode
            pendingString = false;
            pendingNumeric = false;
            cursor += 1;
            continue;
          }
          cursor += 1;
          continue;
        }
        if (sg.code === 140 && pendingNumeric) {
          const numeric = Number.parseFloat(sg.value);
          row.values.push(Number.isFinite(numeric) ? numeric : 0);
          pendingNumeric = false;
          pendingString = false;
          cursor += 1;
          continue;
        }
        cursor += 1;
      }
      // Pad values to columnCount so downstream indexing is safe.
      while (row.values.length < columns.length) row.values.push(0);
      rows.push(row);
    }
  }

  // Bail out if we got no useful columns — surface as undefined so the
  // caller can fall through to the synthetic decoder or the legacy path.
  if (columns.length === 0) return undefined;
  const hasAnyTypedKind = columns.some((c) => c.kind !== "unknown");
  if (!hasAnyTypedKind) return undefined;

  // Determine the key column — it is the `UserVariable` column whose
  // linkHandle points at the driving BLOCKUSERPARAMETER.
  const keyIdx = columns.findIndex((c) => c.kind === "user-variable");
  let keyColumn: BlockPropertiesTableKeyColumn | undefined;
  if (keyIdx >= 0) {
    const kc = columns[keyIdx];
    if (kc && kc.linkHandle.length > 0) {
      keyColumn = {
        columnIndex: keyIdx,
        driverParameterHandle: kc.linkHandle,
        driverParameterName: kc.displayName,
      };
    }
  }

  return {
    entries: [],
    id: obj.handle,
    handle: obj.handle,
    name: displayName,
    columns,
    rows,
    ...(keyColumn ? { keyColumn } : {}),
  };
}

// Map AutoCAD's `301 <marker>` strings (authoritative for Phase 7) into our
// canonical column-kind enum. Comparison is case-insensitive to tolerate
// minor AutoCAD variant spellings.
function columnKindFromMarker(
  marker: string,
): BlockPropertiesTableColumn["kind"] {
  switch (marker.toLowerCase()) {
    case "uservariable":
      return "user-variable";
    case "updateddistance":
      return "updated-distance";
    case "updatedangle":
      return "updated-angle";
    case "updatedreal":
      return "updated-real";
    case "updatedstring":
      return "updated-string";
    default:
      return "unknown";
  }
}

// Synthetic-writer path — kept for round-trip tests that emit the
// 92-kind form. Identical to the pre-Phase-7 decoder but renamed for
// clarity. The real AutoCAD path above is preferred when both patterns
// could match.
function decodeSyntheticPropertiesTable(
  obj: DxfDynamicBlockObject,
): BlockPropertiesTable | undefined {
  const groups = obj.rawGroups;
  const name = firstGroup(groups, 1)?.trim() ?? "";

  // Locate the `90 columnCount` marker. A legacy BTABLE never emits a
  // group-90 on this record (column count sits at 91), so absence of 90
  // means this is not a constraint-based table.
  let cursor = -1;
  let columnCount = 0;
  for (let i = 0; i < groups.length; i += 1) {
    const g = groups[i];
    if (g && g.code === 90) {
      const parsed = Number.parseInt(g.value, 10);
      if (Number.isFinite(parsed)) {
        cursor = i;
        columnCount = parsed;
        break;
      }
    }
  }
  if (cursor < 0) return undefined;

  // Walk column blocks. Each block starts at `91 columnIndex` and is
  // self-terminating: we stop reading its codes when the next `91 idx`,
  // a `93 rowCount`, or end-of-groups is reached.
  const columns: BlockPropertiesTableColumn[] = [];
  let i = cursor + 1;
  while (i < groups.length && columns.length < columnCount) {
    const g = groups[i];
    if (!g) break;
    if (g.code === 93) break; // rows section begins
    if (g.code !== 91) {
      i += 1;
      continue;
    }
    const index = Number.parseInt(g.value, 10);
    const col: BlockPropertiesTableColumn = {
      index: Number.isFinite(index) ? index : columns.length,
      kind: "unknown",
      columnName: "",
      displayName: "",
      linkHandle: "",
      valueType: "unknown",
    };
    i += 1;
    // Each column's sub-groups appear in a fixed order right after the
    // 91 marker; we consume until the next 91/93 or unrelated code.
    while (i < groups.length) {
      const sg = groups[i];
      if (!sg) break;
      if (sg.code === 91 || sg.code === 93) break;
      switch (sg.code) {
        case 92: {
          const kindRaw = Number.parseInt(sg.value, 10);
          col.kind = decodeColumnKind(
            Number.isFinite(kindRaw) ? kindRaw : undefined,
          );
          break;
        }
        case 301:
          col.columnName = sg.value.trim();
          break;
        case 300:
          col.displayName = decodeDxfUnicode(sg.value).trim(); // T9 PR-3 — column name decode
          break;
        case 340:
          col.linkHandle = sg.value.trim();
          break;
        case 170: {
          const vtRaw = Number.parseInt(sg.value, 10);
          col.valueType = decodeValueType(
            Number.isFinite(vtRaw) ? vtRaw : undefined,
          );
          break;
        }
        default:
          // Unknown per-column group — skip; preserve forward compat.
          break;
      }
      i += 1;
    }
    columns.push(col);
  }

  // Without per-column `92 kind` we are looking at a legacy BTABLE with
  // a stray group-90 — refuse to claim the record and let the caller
  // fall through.
  const hasAnyKind = columns.some((c) => c.kind !== "unknown");
  if (columnCount > 0 && !hasAnyKind) return undefined;

  // Row section — locate `93 rowCount`.
  let rowsCursor = -1;
  let rowCount = 0;
  for (; i < groups.length; i += 1) {
    const g = groups[i];
    if (g && g.code === 93) {
      const parsed = Number.parseInt(g.value, 10);
      if (Number.isFinite(parsed)) {
        rowsCursor = i;
        rowCount = parsed;
        break;
      }
    }
  }

  const rows: BlockPropertiesTableRow[] = [];
  if (rowsCursor >= 0) {
    i = rowsCursor + 1;
    while (i < groups.length && rows.length < rowCount) {
      const g = groups[i];
      if (!g) break;
      if (g.code !== 90) {
        i += 1;
        continue;
      }
      const rowIndex = Number.parseInt(g.value, 10);
      const row: BlockPropertiesTableRow = {
        index: Number.isFinite(rowIndex) ? rowIndex : rows.length,
        flag: 1,
        key: "",
        values: [],
      };
      i += 1;
      // First 170 after the row marker is the row state flag; the next
      // 300 is the row display key. Subsequent groups are per-column
      // cell pairs: (170 = cell value-type, 140 = number OR 300 = string).
      let seenRowFlag = false;
      let seenRowKey = false;
      let expectingCellValue = false;
      let pendingCellType: "string" | "numeric" | null = null;
      while (i < groups.length) {
        const sg = groups[i];
        if (!sg) break;
        if (sg.code === 90) break; // next row begins
        if (sg.code === 170) {
          if (!seenRowFlag) {
            const flagRaw = Number.parseInt(sg.value, 10);
            if (Number.isFinite(flagRaw)) row.flag = flagRaw;
            seenRowFlag = true;
          } else {
            // Cell value-type — group 5 means "string cell follows as 300",
            // everything else means "numeric cell follows as 140".
            const vtRaw = Number.parseInt(sg.value, 10);
            pendingCellType = vtRaw === 5 ? "string" : "numeric";
            expectingCellValue = true;
          }
          i += 1;
          continue;
        }
        if (sg.code === 300) {
          if (!seenRowKey) {
            row.key = decodeDxfUnicode(sg.value).trim(); // T9 PR-3 — row key decode
            seenRowKey = true;
            i += 1;
            continue;
          }
          if (expectingCellValue && pendingCellType === "string") {
            row.values.push(decodeDxfUnicode(sg.value)); // T9 PR-3 — string cell decode
            expectingCellValue = false;
            pendingCellType = null;
            i += 1;
            continue;
          }
          // Free-floating 300 without a preceding 170=5 — treat as a
          // string cell fallback so malformed writers still round-trip.
          row.values.push(decodeDxfUnicode(sg.value)); // T9 PR-3 — string cell decode
          i += 1;
          continue;
        }
        if (sg.code === 140) {
          const numeric = Number.parseFloat(sg.value);
          row.values.push(Number.isFinite(numeric) ? numeric : 0);
          expectingCellValue = false;
          pendingCellType = null;
          i += 1;
          continue;
        }
        // Unknown group inside a row — skip.
        i += 1;
      }
      // Pad to match declared column count so downstream code can index
      // values by column position without defensive checks.
      while (row.values.length < columns.length) {
        row.values.push(0);
      }
      rows.push(row);
    }
  }

  // Key-column — the UserVariable column (usually index 0). Skip when
  // no column declares that kind or the link handle is empty (AutoCAD
  // authored without a driver parameter — graceful fallback).
  const keyIdx = columns.findIndex((c) => c.kind === "user-variable");
  let keyColumn: BlockPropertiesTableKeyColumn | undefined;
  if (keyIdx >= 0) {
    const kc = columns[keyIdx];
    if (kc && kc.linkHandle.length > 0) {
      keyColumn = {
        columnIndex: keyIdx,
        driverParameterHandle: kc.linkHandle,
        driverParameterName: kc.displayName,
      };
    }
  }

  return {
    entries: [],
    id: obj.handle,
    handle: obj.handle,
    name,
    columns,
    rows,
    ...(keyColumn ? { keyColumn } : {}),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Dispatch tables — exported for reuse in the top-level parser
// ═══════════════════════════════════════════════════════════════════════════

export const TIER2_PARAMETER_CLASSES = new Set([
  "ACDBBLOCKPOINTPARAMETER",
  "ACDBBLOCKPOLARPARAMETER",
  "ACDBBLOCKXYPARAMETER",
  "ACDBBLOCKROTATIONPARAMETER",
  "ACDBBLOCKLOOKUPPARAMETER",
  // DYN-2 Batch C — dimensional constraint parameters
  "ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER",
  "ACDBBLOCKVERTICALCONSTRAINTPARAMETER",
  "ACDBBLOCKANGULARCONSTRAINTPARAMETER",
  // DYN-4 Phase 3 Fix 4 — AutoCAD 2018+ un-prefixed radial constraint
  // lives under tier-2 dispatch because it shares the parser.constraints
  // BPT-form decoder path with horizontal / vertical / angular. Tier 3's
  // `ACDBBLOCKCONSTRAINEDRADIAL` (older AutoCAD vintage) remains in
  // TIER3_PARAMETER_CLASSES for that older shape.
  "ACDBBLOCKRADIALCONSTRAINTPARAMETER",
]);

export const TIER2_ACTION_CLASSES = new Set([
  "ACDBBLOCKROTATIONACTION",
  "ACDBBLOCKSCALEACTION",
  "ACDBBLOCKARRAYACTION",
  "ACDBBLOCKLOOKUPACTION",
]);

// Note: `ACDBBLOCKPROPERTIESTABLEGRIP` was historically included here as a
// placeholder for the BlockPropertiesTable's companion grip, but AutoCAD
// actually emits it as a distinct OBJECTS record with the AcDbBlockGrip
// subclass marker (position + owner + eval refs). DYN-4 Phase 4b routes
// it through `dxfBridge.dynamic.parser.grips.ts` so it lands on
// `metadata.grips[]` with `kind === "properties-table"` instead of being
// mis-parsed as a table.
export const TIER2_TABLE_CLASSES = new Set(["ACDBBLOCKPROPERTIESTABLE"]);

/**
 * DYN-4 Phase 3 — detect "real AutoCAD 2018+" BPT-form encoding vs. the
 * synthetic writer round-trip shape.
 *
 * Real AutoCAD output (АБИ1.dxf) carries:
 *   - group 300 for the parameter name (vs. synthetic group 1)
 *   - group 1010/1020 for basePoint (vs. synthetic 10/20)
 *
 * Presence of EITHER code is enough to route the record through
 * `parser.constraints.ts`; a synthetic writer round-trip (which only
 * emits 1/10/11/20/21/140/141) falls through to the legacy Batch-C
 * parsers below. A BPT-form record missing one of the two codes still
 * hits the BPT parser because `pointWithLegacyFallback` handles the
 * partial-shape case.
 */
function hasBptConstraintShape(obj: DxfDynamicBlockObject): boolean {
  for (const g of obj.rawGroups) {
    if (g.code === 300 && g.value.trim().length > 0) return true;
    if (g.code === 1010) return true;
  }
  return false;
}

/**
 * Tier 2 parameter dispatcher. Returns `undefined` when the object is not
 * a Tier 2 parameter (so the caller can fall through to Tier 1 / unknown).
 *
 * For dimensional constraint parameters we prefer the BPT-form parsers
 * (`parser.constraints.ts`) whenever the record shows the AutoCAD 2018+
 * shape (group 300 name / 1010 basePoint). Synthetic writer round-trip
 * objects fall through to the legacy Batch-C decoders so existing tests
 * that exercise the 1/10/20/11/21/140/141 encoding keep passing.
 */
export function tryParseTier2Parameter(
  obj: DxfDynamicBlockObject,
  tableHandleToId: Map<string, string>,
): DynamicParameter | undefined {
  const canonical = canonicalDynamicBlockClass(obj.type);
  switch (canonical) {
    case "ACDBBLOCKPOINTPARAMETER":
      return parsePointParameter(obj);
    case "ACDBBLOCKPOLARPARAMETER":
      return parsePolarParameter(obj);
    case "ACDBBLOCKXYPARAMETER":
      return parseXYParameter(obj);
    case "ACDBBLOCKROTATIONPARAMETER":
      return parseRotationParameter(obj);
    case "ACDBBLOCKLOOKUPPARAMETER":
      return parseLookupParameter(obj, tableHandleToId);
    case "ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER":
      return hasBptConstraintShape(obj)
        ? parseHorizontalConstraintParameterFromBpt(obj)
        : parseHorizontalConstraintParameter(obj);
    case "ACDBBLOCKVERTICALCONSTRAINTPARAMETER":
      return hasBptConstraintShape(obj)
        ? parseVerticalConstraintParameterFromBpt(obj)
        : parseVerticalConstraintParameter(obj);
    case "ACDBBLOCKANGULARCONSTRAINTPARAMETER":
      return hasBptConstraintShape(obj)
        ? parseAngularConstraintParameterFromBpt(obj)
        : parseAngularConstraintParameter(obj);
    case "ACDBBLOCKRADIALCONSTRAINTPARAMETER":
      // АБИ1.dxf shape — only the BPT-form decoder handles this class.
      // The older tier-3 `ACDBBLOCKCONSTRAINEDRADIAL` stays in
      // `tryParseTier3Parameter` for synthetic legacy fixtures.
      return parseRadialConstraintParameterFromBpt(obj);
    default:
      return undefined;
  }
}

/**
 * Tier 2 action dispatcher. Returns `undefined` when the object is not a
 * Tier 2 action.
 */
export function tryParseTier2Action(
  obj: DxfDynamicBlockObject,
  handleToParamId: Map<string, string>,
):
  | RotateAction
  | ScaleAction
  | ArrayAction
  | LookupAction
  | undefined {
  switch (canonicalDynamicBlockClass(obj.type)) {
    case "ACDBBLOCKROTATIONACTION":
      return parseRotateAction(obj, handleToParamId);
    case "ACDBBLOCKSCALEACTION":
      return parseScaleAction(obj, handleToParamId);
    case "ACDBBLOCKARRAYACTION":
      return parseArrayAction(obj, handleToParamId);
    case "ACDBBLOCKLOOKUPACTION":
      return parseLookupAction(obj, handleToParamId);
    default:
      return undefined;
  }
}

